import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Copy, KeyRound, RotateCcw, Loader } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from './ThemedText';
import { Button } from './Button';
import { organizationService } from '../api';

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  &:first-of-type { margin-top: 0; }
`;

const CredBox = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.sizes.small};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CredRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  code {
    font-family: monospace;
    color: ${({ theme }) => theme.colors.tint};
    word-break: break-all;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.tint};
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  &:hover { text-decoration: underline; }
`;

const Hint = styled(ThemedText).attrs({ type: 'small' })`
  color: ${({ theme }) => theme.colors.muted};
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const FreshPassword = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.tint};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sizes.small};
`;

/**
 * SuperAdmin credentials panel for a demo org (Demo Studio editor → Credentials, edit mode).
 * Viewer creds are public by design; the builder password shows the shared config default while it
 * still applies, and a reset issues a new password displayed exactly once (never stored plaintext).
 */
const DemoCredentialsPanel = ({ orgId }) => {
  const theme = useTheme();
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  // Plaintext from the last reset — shown once, kept only in component state.
  const [freshPassword, setFreshPassword] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await organizationService.getDemoCredentials(orgId);
        if (!cancelled) setCreds(data);
      } catch (error) {
        console.error('Failed to load demo credentials:', error);
        if (!cancelled) toast.error(error?.message || 'Failed to load demo credentials');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const copy = (text, label = 'Copied!') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the builder password? The current password stops working immediately.')) return;
    setResetting(true);
    try {
      const data = await organizationService.resetDemoBuilderPassword(orgId);
      setCreds(data);
      setFreshPassword(data.builderPassword);
      toast.success('Builder password reset');
    } catch (error) {
      console.error('Failed to reset builder password:', error);
      toast.error(error?.message || 'Failed to reset the builder password');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <ThemedText><Loader size={14} /> Loading credentials…</ThemedText>;
  }
  if (!creds) {
    return <ThemedText>Credentials are unavailable for this organization.</ThemedText>;
  }

  const builderPassword = freshPassword || creds.builderPassword;

  return (
    <>
      <SectionTitle theme={theme}>Viewer login (read-only — shown publicly)</SectionTitle>
      <CredBox theme={theme}>
        <CredRow theme={theme}>
          <strong>Email:</strong> <code>{creds.viewerEmail || '—'}</code>
          {creds.viewerEmail && (
            <CopyButton theme={theme} type="button" onClick={() => copy(creds.viewerEmail, 'Viewer email copied')}>
              <Copy size={12} /> Copy
            </CopyButton>
          )}
        </CredRow>
        <CredRow theme={theme}>
          <strong>Password:</strong> <code>{creds.viewerPassword || '—'}</code>
          {creds.viewerPassword && (
            <CopyButton theme={theme} type="button" onClick={() => copy(creds.viewerPassword, 'Viewer password copied')}>
              <Copy size={12} /> Copy
            </CopyButton>
          )}
        </CredRow>
      </CredBox>
      <Hint theme={theme}>Public read-only credentials — also displayed on the demo landing page.</Hint>

      <SectionTitle theme={theme}>Builder login (internal, read-write)</SectionTitle>
      <CredBox theme={theme}>
        <CredRow theme={theme}>
          <strong>Email:</strong> <code>{creds.builderEmail}</code>
          <CopyButton theme={theme} type="button" onClick={() => copy(creds.builderEmail, 'Builder email copied')}>
            <Copy size={12} /> Copy
          </CopyButton>
        </CredRow>
        <CredRow theme={theme}>
          <strong>Password:</strong>{' '}
          {builderPassword ? (
            <>
              <code>{builderPassword}</code>
              <CopyButton theme={theme} type="button" onClick={() => copy(builderPassword, 'Builder password copied')}>
                <Copy size={12} /> Copy
              </CopyButton>
            </>
          ) : (
            <span>
              <KeyRound size={12} style={{ verticalAlign: '-2px' }} /> Custom password set — reset to view a new one.
            </span>
          )}
        </CredRow>
      </CredBox>
      {freshPassword ? (
        <FreshPassword theme={theme}>
          This new password is shown <strong>once</strong> — copy it now. It is stored only as a hash.
        </FreshPassword>
      ) : (
        <Hint theme={theme}>
          {creds.builderPasswordIsDefault
            ? 'This demo still uses the shared builder password from server config.'
            : 'This demo has its own builder password.'}
        </Hint>
      )}

      <div style={{ marginTop: theme.spacing.md }}>
        <Button type="button" variant="secondary" onClick={handleReset} disabled={resetting}>
          <RotateCcw size={14} /> {resetting ? 'Resetting…' : 'Reset builder password'}
        </Button>
      </div>
    </>
  );
};

export default DemoCredentialsPanel;
export { DemoCredentialsPanel };
